import React from 'react';
import { TableCell, TableRow, Typography, Box, ListItem } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import useAppTheme from 'hooks/useAppTheme';
import CloseIcon from '@mui/icons-material/Close';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  item: any;
  subItems?: any[];
  onItemClick?: (itemName: string) => void;
  selectedItemName?: string | null;
}

const listItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: '3px',
};

const dotStyle = (color: string) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8,
});

interface DirectionIconProps {
  direction: 'up' | 'down' | 'none';
  itemName: string;
}

const DirectionIcon = ({ direction, itemName }: DirectionIconProps) => {
  const productiveNames = ['Productive', 'Engine Hours', 'Productive Hours'];

  if (direction === 'none') {
    return null;
  }

  const isProductive = productiveNames.includes(itemName);

  if (direction === 'up') {
    return (
      <NorthEastIcon
        sx={{
          color: isProductive ? '#029161' : '#EB5757',
          fontSize: 10,
        }}
      />
    );
  }

  return (
    <SouthEastIcon
      sx={{
        color: isProductive ? '#EB5757' : '#029161',
        fontSize: 10,
      }}
    />
  );
};

const TableItem: React.FC<Props> = ({
  item,
  subItems = [],
  onItemClick,
  selectedItemName,
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <>
      <TableRow
        sx={{
          border: 'none',
        }}
      >
        <TableCell
          sx={{
            border: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: item.color,
                borderRadius: '50%',
                marginRight: '8px',
              }}
            />
            <Typography
              color="#d1d5db"
              sx={{
                fontSize: '10px',
                color: palette.textSecondary,
              }}
            >
              {item.displayName ?? item.name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          align="right"
          sx={{
            border: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              color="#d1d5db"
              sx={{
                marginRight: '4px',
                fontSize: '10px',
                color: palette.textSecondary,
              }}
            >
              {item.time}
            </Typography>
            <DirectionIcon direction={item.direction} itemName={item.name} />
          </Box>
        </TableCell>
      </TableRow>
      {/* Child Items */}
      {subItems.length > 0 && (
        <>
          {subItems.map((item, index) => {
            if (item.name === 'Unknown' && item.value === 0) {
              return <></>;
            }
            return (
              <TableRow
                key={index}
                onClick={() => {
                  if (item.disabled) return;
                  onItemClick?.(item.name);
                }}
                sx={{
                  border: 'none',
                  cursor: item.disabled
                    ? 'default'
                    : onItemClick
                    ? 'pointer'
                    : 'default',
                  opacity: item.disabled ? 0.6 : 1,
                  backgroundColor:
                    item.name === selectedItemName
                      ? enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.1)'
                      : 'transparent',
                  '&:hover': {
                    backgroundColor: onItemClick
                      ? enableDarkTheme
                        ? item.name === selectedItemName
                          ? 'rgba(255, 255, 255, 0.15)'
                          : 'rgba(255, 255, 255, 0.05)'
                        : item.name === selectedItemName
                        ? 'rgba(0, 0, 0, 0.15)'
                        : 'rgba(0, 0, 0, 0.05)'
                      : 'transparent',
                  },
                }}
              >
                <TableCell
                  sx={{
                    border: 'none',
                    pl: 4,
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Box sx={dotStyle(item.color)} />
                    <Typography
                      color={palette.textPrimary}
                      sx={{
                        fontSize: '10px',
                        fontWeight:
                          item.name === selectedItemName ? '600' : '400',
                      }}
                    >
                      {item.displayName ?? item.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    border: 'none',
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Typography
                      color={palette.textPrimary}
                      sx={{
                        fontSize: '10px',
                        fontWeight:
                          item.name === selectedItemName ? '600' : '400',
                        marginRight: '4px',
                      }}
                    >
                      {item.value}
                    </Typography>
                    <DirectionIcon
                      direction={item.direction}
                      itemName={item.name}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </>
      )}
    </>
  );
};

export default TableItem;
